import { createFileRoute, useRouter } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  listFeatureFlags, upsertFeatureFlag, deleteFeatureFlag, type FeatureFlag,
} from "@/lib/feature-flags.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/feature-flags")({
  head: () => ({ meta: [{ title: "Feature flags — Admin" }] }),
  component: AdminFeatureFlagsPage,
});

function AdminFeatureFlagsPage() {
  const listFn = useServerFn(listFeatureFlags);
  const upsert = useServerFn(upsertFeatureFlag);
  const remove = useServerFn(deleteFeatureFlag);
  const router = useRouter();

  const { data: flags = [], refetch } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: () => listFn({}),
  });

  const [draft, setDraft] = useState({ key: "", description: "", rollout_percent: 0 });

  async function toggle(f: FeatureFlag, next: boolean) {
    const res = await upsert({ data: { key: f.key, enabled: next, description: f.description, rollout_percent: f.rollout_percent } });
    if ("error" in res) toast.error(res.error); else { toast.success(`${f.key} → ${next ? "on" : "off"}`); refetch(); router.invalidate(); }
  }

  async function create() {
    if (!draft.key.trim()) return;
    const res = await upsert({ data: { key: draft.key.trim(), enabled: false, description: draft.description || null, rollout_percent: draft.rollout_percent } });
    if ("error" in res) toast.error(res.error);
    else { toast.success("Flag created"); setDraft({ key: "", description: "", rollout_percent: 0 }); refetch(); }
  }

  async function del(key: string) {
    if (!confirm(`Delete flag "${key}"?`)) return;
    const res = await remove({ data: { key } });
    if ("error" in res) toast.error(res.error); else { toast.success("Deleted"); refetch(); }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-10 p-6">
      <BackToDashboard />
      <header>
        <p className="text-[0.6rem] tracking-wide-luxury uppercase text-accent">Admin</p>
        <h1 className="text-3xl font-medium">Feature flags</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Dark-launch changes without a redeploy. Flags are readable by any signed-in user; only admins can toggle.
        </p>
      </header>

      <section className="border border-border p-6 space-y-4">
        <h2 className="text-sm uppercase tracking-wide-luxury text-accent">New flag</h2>
        <div className="grid gap-3 md:grid-cols-[1fr_2fr_120px_auto]">
          <div><Label>Key</Label><Input value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value })} placeholder="e.g. instant_payouts" /></div>
          <div><Label>Description</Label><Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
          <div><Label>Rollout %</Label><Input type="number" min={0} max={100} value={draft.rollout_percent}
            onChange={(e) => setDraft({ ...draft, rollout_percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} /></div>
          <div className="flex items-end"><Button onClick={create}>Create</Button></div>
        </div>
      </section>

      <section className="border border-border">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide-luxury text-muted-foreground">
            <tr><th className="p-3">Key</th><th className="p-3">Description</th><th className="p-3">Rollout</th><th className="p-3">On</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {flags.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No flags yet.</td></tr>}
            {flags.map((f) => (
              <tr key={f.key} className="border-t border-border">
                <td className="p-3 font-mono text-xs">{f.key}</td>
                <td className="p-3 text-muted-foreground">{f.description ?? "—"}</td>
                <td className="p-3 tabular-nums">{f.rollout_percent}%</td>
                <td className="p-3"><Switch checked={f.enabled} onCheckedChange={(v) => toggle(f, v)} /></td>
                <td className="p-3 text-right"><Button variant="ghost" size="sm" onClick={() => del(f.key)}>Delete</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
